import type { Competitor } from '../types'

// Sources (checked 2026-10-01): feds.lol homepage pricing section
// (feds.lol/#premium; /pricing returns 404), feds.lol terms of service, and the
// official site's own page bundles for fonts, effects, and widgets. The feds.lol
// docs site was unreachable on the check date.
const fedslol: Competitor = {
  slug: 'fedslol',
  aliases: ['feds', 'feds-lol'],
  name: 'feds.lol',
  tagline: 'Aesthetic biolink with a big widget catalog and an $8 one-time Premium.',
  site: 'https://feds.lol',
  pricingUrl: 'https://feds.lol/#premium',
  checkedOn: '2026-10-01',
  seo: {
    title: 'feds.lol alternative: mychar.gg vs feds.lol compared (2026)',
    description: 'A feds.lol alternative with a music player on every plan, video clips, custom domains, and built-in analytics. Features and one-time prices compared.'
  },
  hero: {
    title: 'The feds.lol alternative with more on the free plan',
    description: 'Music on every plan, clips that play in place, analytics you can act on, and your own domain, all designed in a live drag-and-drop editor.'
  },
  summary: 'mychar.gg and feds.lol are both one-time-payment biolinks with effects, cursors, and widgets. mychar.gg puts a music player on every plan, adds video clips that play on the page, a custom domain option, and analytics with clicks and top links, and its Elite tier has 13 background effects. feds.lol has the bigger widget catalog, more text animations, and Premium at $8 once against €6.99 for mychar.gg Pro.',
  chooseMychar: [
    'want a music player without paying for Premium',
    'want video clips that play on the page',
    'want your own domain on your page',
    'want analytics with clicks, top links, and live visitors'
  ],
  chooseThem: [
    'want widgets for Steam, Valorant, GitHub, Last.fm, and more',
    'want 25 text animations and an audio visualizer',
    'want Spotify track and playlist widgets'
  ],
  features: {
    free_plan: { yes: true, text: 'Widgets, fonts, basic animations' },
    paid_plans: 'Premium $8, once',
    billing: 'Once',
    first_year: '$8 in total (Premium)',
    custom_domain: { yes: false, text: 'Not listed' },
    editor: 'Settings with a live preview',
    themes: { yes: true, text: 'Community templates' },
    fonts: '29 built in (9 on Free)',
    custom_fonts: { yes: true, text: 'From a URL, Premium' },
    effects: { yes: true, text: '10 on Premium, overlays on Free' },
    cursors: { yes: true, text: 'Premium' },
    video_background: { yes: true, text: 'Premium' },
    custom_code: false,
    music_player: { yes: true, text: 'Background audio on Premium' },
    video_clips: { yes: false, text: 'YouTube widget' },
    comments: true,
    view_counter: true,
    badges: { yes: true, text: 'Premium badge' },
    youtube: { yes: true, text: 'Channel and video widgets' },
    twitch: { yes: true, text: 'Channel widget' },
    discord: { yes: true, text: 'Presence and server widgets' },
    music_embeds: { yes: true, text: 'Spotify and SoundCloud widgets' }
  },
  plans: [
    { name: 'Free', price: '$0', period: 'forever', note: 'Theme colors and fonts, widgets and socials, layout options, overlays, basic animations.' },
    { name: 'Premium', price: '$8', period: 'one-time', note: 'Video and audio backgrounds, 10 background effects, 10 cursor trails, 29 fonts, text animations.' }
  ],
  plansNote: 'USD. Per its terms, feds.lol purchases are not refundable.',
  advantages: [
    {
      title: 'A music player on every plan',
      description: 'Upload a track on Free, 3 on Pro, and 6 on Elite. Background audio is a Premium feature on feds.lol.',
      icon: 'i-lucide-music'
    },
    {
      title: 'Video clips that play in place',
      description: 'Upload highlights that visitors watch on your page, up to 18 on Elite.',
      icon: 'i-lucide-clapperboard'
    },
    {
      title: 'Your own domain',
      description: 'Put your page on a domain you own for €2.99 per month, on any plan.',
      icon: 'i-lucide-globe'
    },
    {
      title: 'Analytics built in',
      description: 'Views, clicks, and top links on every plan, plus live visitors, countries, and referrers on Pro.',
      icon: 'i-lucide-chart-line'
    },
    {
      title: 'Edit the page itself',
      description: 'Change text where it appears and drag sections, links, and widgets into place while the page updates live.',
      icon: 'i-lucide-mouse-pointer-click'
    },
    {
      title: 'Extras for creators, streamers, and gamers',
      description: 'A game library with your handles, a setup section with product links, friends, achievements, and community skins.',
      icon: 'i-lucide-users'
    }
  ],
  strengths: [
    {
      title: 'The widest widget catalog',
      description: 'feds.lol has widgets for Spotify, SoundCloud, GitHub, Steam, Roblox, Valorant, Last.fm, MyAnimeList, Chess.com, weather, and more.'
    },
    {
      title: 'Lots of motion',
      description: 'Premium includes 25 text animations, an audio visualizer, 10 background effects, and 10 cursor trails.'
    },
    {
      title: 'A cheap Premium tier',
      description: 'Everything on Premium costs $8 once.'
    },
    {
      title: 'Comments and templates',
      description: 'Pages can have comments, and community templates give you a starting point.'
    }
  ],
  sections: [
    {
      title: 'Why people look for a feds.lol alternative',
      paragraphs: [
        'feds.lol packs a lot into a biolink, especially widgets, and its Premium tier is a one-time $8. The reasons to look around tend to be specific. Background audio and video are Premium-only, so a free page has no music. Your page lives on feds.lol with no custom domain option, and video on the page comes from YouTube widgets rather than your own uploads.',
        'Analytics are the other question. feds.lol lists detailed analytics without saying which plan includes them. On mychar.gg, views, clicks, and top links are on every plan, and Pro adds live visitors, countries, and referrers.'
      ]
    },
    {
      title: 'How mychar.gg is different',
      paragraphs: [
        'mychar.gg gives every plan a music player and analytics, and Pro and Elite add uploaded video clips that play in place. You build in an [editor](/docs/customization) that is your page in edit mode, picking layouts, fonts, colors, backgrounds, effects, and cursor trails while you watch it update. Elite has 13 background effects and 10 cursor trails, and you can put the whole page on your own domain for €2.99 per month.',
        'There are fewer widgets than on feds.lol, but the sections that are there go deep: YouTube with your latest uploads, Twitch with live status and schedule, Discord with your profile card, a game library with your in-game handles, and a setup section for your gear. The [guide for gamers](/for/gamers) and the [guide for streamers](/for/streamers) show how they fit together.'
      ]
    }
  ],
  switchSteps: [
    { title: 'Claim your username', description: 'Sign up free at [mychar.gg/signup](/signup). If your feds.lol name is free, take it here too.' },
    { title: 'Bring your assets', description: 'Upload your avatar, background, cursor, and music. Video backgrounds and cursor uploads start on Pro.' },
    { title: 'Rebuild the look', description: 'Pick a layout, font, and colors, add an effect and a cursor trail, or start from a community [skin](/skins).' },
    { title: 'Swap widgets for sections', description: 'Connect YouTube, Twitch, and Discord, add your games with handles, and list your setup.' },
    { title: 'Swap the link', description: 'Replace your feds.lol link in your bios, or connect your own domain with the [custom domain add-on](/docs/profile/custom-domain).' }
  ],
  faq: [
    {
      label: 'Is mychar.gg cheaper than feds.lol?',
      content: 'They are close. Both are one-time payments: mychar.gg Pro is €6.99 and Elite €12.99, and feds.lol Premium is $8. mychar.gg includes a music player and analytics on the free plan.'
    },
    {
      label: 'Does mychar.gg have widgets like feds.lol?',
      content: 'It has fewer, deeper sections instead: YouTube, Twitch, Discord, a game library, a setup section, clips, and a music player you can drag to any corner.'
    },
    {
      label: 'Can I keep comments on my page?',
      content: 'Yes. The comment section is on Pro and Elite, and you moderate it from your dashboard. See the [comments guide](/docs/profile/comments).'
    },
    {
      label: 'Can I use my own domain?',
      content: 'Yes. The custom domain add-on is €2.99 per month on any plan and keeps your mychar.gg link working.'
    }
  ]
}

export default fedslol
